import React, { useState } from 'react';
import { styled, useTheme } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Drawer from '@mui/material/Drawer';
import CssBaseline from '@mui/material/CssBaseline';
import MuiAppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import List from '@mui/material/List';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import Avatar from '@mui/material/Avatar';
import Tooltip from '@mui/material/Tooltip';
import MenuIcon from '@mui/icons-material/Menu';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ListItem from '@mui/material/ListItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import DashboardIcon from '@mui/icons-material/Dashboard';
import AssignmentIcon from '@mui/icons-material/Assignment';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import useCheckSuperAdmin from '../../hooks/useCheckSuperAdmin';
import SchoolSelectInfoBar from '../UI/SchoolSelectInfoBar';
import Logo from '../Logo/Logo';

const drawerWidth = 240;

const Main = styled('main', { shouldForwardProp: (prop) => prop !== 'open' })(
  ({ theme, open }) => ({
    flexGrow: 1,
    width: '100%',
    minWidth: 0,
    minHeight: '100vh',
    padding: theme.spacing(3),
    backgroundColor: theme.palette.background.default,
    transition: theme.transitions.create('margin', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    marginLeft: `-${drawerWidth}px`,
    ...(open && {
      transition: theme.transitions.create('margin', {
        easing: theme.transitions.easing.easeOut,
        duration: theme.transitions.duration.enteringScreen,
      }),
      marginLeft: 0,
    }),
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(2),
    },
  }),
);

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== 'open',
})(({ theme, open }) => ({
  transition: theme.transitions.create(['margin', 'width'], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  ...(open && {
    width: `calc(100% - ${drawerWidth}px)`,
    marginLeft: `${drawerWidth}px`,
    transition: theme.transitions.create(['margin', 'width'], {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
  }),
}));

const DrawerHeader = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  minHeight: 56,
  padding: theme.spacing(0.5, 1),
  justifyContent: 'space-between',
}));

const getInitials = (name) => {
  if (!name) return 'U';

  const displayName = name.split('@')[0].replace(/[._-]+/g, ' ').trim();
  const parts = displayName.split(/\s+/).filter(Boolean);

  return parts.slice(0, 2).map((part) => part[0]).join('').toUpperCase() || 'U';
};

export default function MainLayout({ children }) {
  const theme = useTheme();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const ifSuperAdmin = useCheckSuperAdmin();
  const { userData } = useSelector((state) => state.user);
  const userName = userData?.displayname || userData?.username || 'Logged in user';

  const menuItems = [
    { text: 'Browse Forms', icon: <DashboardIcon />, path: '/' },
    { text: 'Responses', icon: <AssignmentIcon />, path: '/Responses/:id' },
  ];

  const isItemActive = (item) => {
    if (item.path === '/') return location.pathname === '/';
    return location.pathname.startsWith('/Responses') || location.pathname.startsWith('/Response/');
  };

  return (
    <Box sx={{ display: 'flex' }}>
      <CssBaseline />
      <AppBar position="fixed" open={open} elevation={0}>
        <Toolbar sx={{ minHeight: { xs: 64, sm: 68 } }}>
          <IconButton
            color="inherit"
            aria-label="open navigation"
            onClick={() => setOpen(true)}
            edge="start"
            sx={{ mr: 2, ...(open && { display: 'none' }) }}
          >
            <MenuIcon />
          </IconButton>
          <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center', minWidth: 0 }}>
            <Logo height="40px" />
            <Typography variant="h6" noWrap component="div" sx={{ ml: 2, letterSpacing: '-0.01em' }}>
              Surveys
            </Typography>
          </Box>
          <Tooltip title={userName} arrow>
            <Avatar
              aria-label={`Logged in as ${userName}`}
              sx={{
                width: 36,
                height: 36,
                ml: 2,
                bgcolor: 'common.white',
                color: 'primary.main',
                border: '2px solid rgba(255,255,255,0.35)',
                fontSize: 14,
                fontWeight: 800,
              }}
            >
              {getInitials(userName)}
            </Avatar>
          </Tooltip>
        </Toolbar>
        {ifSuperAdmin && (
          <Box sx={{ bgcolor: 'warning.main', p: 1 }}>
            <SchoolSelectInfoBar />
          </Box>
        )}
      </AppBar>

      <Drawer
        sx={{
          width: drawerWidth,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: drawerWidth,
            boxSizing: 'border-box',
            borderRight: '1px solid',
            borderColor: 'divider',
            bgcolor: 'background.paper',
          },
        }}
        variant="persistent"
        anchor="left"
        open={open}
      >
        <DrawerHeader>
          <Logo height="34px" />
          <IconButton aria-label="close navigation" onClick={() => setOpen(false)}>
            {theme.direction === 'ltr' ? <ChevronLeftIcon /> : <ChevronRightIcon />}
          </IconButton>
        </DrawerHeader>
        <Divider />
        <List sx={{ pt: 1 }}>
          {menuItems.map((item) => (
            <ListItem
              button
              key={item.text}
              selected={isItemActive(item)}
              onClick={() => navigate(item.path)}
              sx={{
                mx: 1,
                mb: 0.5,
                width: 'auto',
                borderRadius: 2,
                '&.Mui-selected': {
                  bgcolor: 'primary.light',
                  color: 'primary.dark',
                },
                '&.Mui-selected:hover': { bgcolor: 'primary.light' },
              }}
            >
              <ListItemIcon sx={{ minWidth: 40, color: 'inherit' }}>
                {item.icon}
              </ListItemIcon>
              <ListItemText primary={item.text} />
            </ListItem>
          ))}
        </List>
      </Drawer>

      <Main open={open}>
        <Toolbar sx={{ minHeight: { xs: 64, sm: 68 } }} />
        {ifSuperAdmin && <Box sx={{ height: '60px' }} />}
        <Box sx={{ width: '100%', maxWidth: 1440, mx: 'auto' }}>
          {children}
        </Box>
      </Main>
    </Box>
  );
}
